import React, { useEffect, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Switch, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from './InsetModal';
import { AiBotIcon } from './icons/AppIcon';
import { apiService, AiAgent } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing, typography, withAlpha } from '../theme';
import { designTokens } from '../theme/design-system';

/**
 * An AI agent's settings: who it is (name, tone, instructions, language), where and when it
 * answers (channels, numbers, hours), what it may do, which products it offers, and when and to
 * whom it hands a contact over. Saving updates the agent's generated flow.
 */

const ACTIONS = ['show_products', 'cart', 'orders', 'quotations', 'order_status', 'media'] as const;
type Option = { id: string; label: string };

export function AiAgentSettingsModal({ agent, onClose, onSaved }: { agent: AiAgent | null; onClose: () => void; onSaved: (agent: AiAgent) => void }) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { showNotification } = useAppContext();
  const businessId = useAppStore((s) => s.currentBusiness?._id);
  const [draft, setDraft] = useState<AiAgent | null>(agent);
  const [keywords, setKeywords] = useState('');
  const [saving, setSaving] = useState(false);
  const [numbers, setNumbers] = useState<Array<Option & { channel: string }>>([]);
  const [catalogues, setCatalogues] = useState<Option[]>([]);
  const [people, setPeople] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);

  useEffect(() => {
    setDraft(agent);
    setKeywords((agent?.handoff.keywords || []).join(', '));
  }, [agent]);

  useEffect(() => {
    if (!agent) return;
    apiService.getWhatsAppPhoneNumbers().then((res) => {
      const rows: any[] = Array.isArray(res.data) ? res.data : [];
      const wa = rows.filter((p) => p.phoneNumberId).map((p) => ({ id: String(p.phoneNumberId), label: p.displayPhoneNumber || p.verifiedName || p.phoneNumberId, channel: 'whatsapp' }));
      apiService.getFacebookPages().then((pages) => {
        const fb = (Array.isArray(pages.data) ? pages.data : []).filter((p: any) => p.pageId || p.id).map((p: any) => ({ id: String(p.pageId || p.id), label: p.name || p.pageName || p.pageId, channel: 'facebook_messenger' }));
        setNumbers([...wa, ...fb]);
      }).catch(() => setNumbers(wa));
    }).catch(() => undefined);
    apiService.getCatalogues({ limit: 100, sortBy: 'name', sortOrder: 'asc' }).then((res) => setCatalogues((res.data?.catalogues || []).map((c: any) => ({ id: String(c._id || c.id), label: c.name })))).catch(() => undefined);
    if (businessId) {
      apiService.getBusinessUsers(businessId).then((res) => setPeople((res.data || []).filter((u) => !u.isAiAgent && u.role !== 'ai_agent' && u.userStatus === 'active')
        .map((u) => ({ id: u.userId, label: [u.user?.firstName, u.user?.lastName].filter(Boolean).join(' ') || u.user?.email || u.userId })))).catch(() => undefined);
      apiService.getDepartments(businessId).then((res) => setDepartments((res.data || []).map((d: any) => ({ id: String(d._id || d.id), label: d.name })))).catch(() => undefined);
    }
  }, [agent, businessId]);

  if (!draft) return null;
  const ui: UI = { styles, rtl };
  const set = (patch: Partial<AiAgent>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setHandoff = (patch: Partial<AiAgent['handoff']>) => setDraft((d) => (d ? { ...d, handoff: { ...d.handoff, ...patch } } : d));
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const save = async () => {
    if (!draft.name.trim()) return showNotification('error', t('common.error'), t('aiAgents.form.nameRequired'));
    setSaving(true);
    try {
      const res = await apiService.updateAiAgent(draft.id, {
        name: draft.name.trim(), tone: draft.tone, instructions: draft.instructions, language: draft.language, presentation: draft.presentation,
        channels: draft.channels, channelIdentifiers: draft.channelIdentifiers, hours: draft.hours,
        actions: draft.actions, catalogueIds: draft.catalogueIds, isDefault: draft.isDefault, paused: draft.paused,
        handoff: { ...draft.handoff, keywords: keywords.split(/[,،\n]/).map((k) => k.trim()).filter(Boolean) },
      });
      if (res.statusCode !== 1000 || !res.data) throw new Error((res.errors as any)?.[0]?.message || (res.errors as any)?.[0] || t('aiAgents.actionFailed'));
      onSaved(res.data);
      showNotification('success', t('common.success'), t('aiAgents.form.saved'));
      onClose();
    } catch (error: any) {
      showNotification('error', t('common.error'), error?.message || t('aiAgents.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  const shownNumbers = numbers.filter((n) => !draft.channels.length || draft.channels.includes(n.channel));

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
            <AiBotIcon size={18} color={colors.primary as string} />
            <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('aiAgents.form.title', { name: agent?.name || '' })}</Text>
            <TouchableOpacity onPress={onClose} accessibilityLabel={t('common.close')}>
              <Ionicons name="close" size={22} color={colors.text.secondary as string} />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.body} contentContainerStyle={{ gap: spacing.md, paddingBottom: spacing.md }} keyboardShouldPersistTaps="handled">
            <Section ui={ui} title={t('aiAgents.form.persona')}>
              <TextInput style={[styles.input, { textAlign: rtl.textStart }]} value={draft.name} onChangeText={(v) => set({ name: v })} maxLength={60} placeholder={t('aiAgents.form.name')} placeholderTextColor={colors.text.secondary as string} />
              <TextInput style={[styles.input, { textAlign: rtl.textStart }]} value={draft.tone} onChangeText={(v) => set({ tone: v })} maxLength={200} placeholder={t('aiAgents.form.tone')} placeholderTextColor={colors.text.secondary as string} />
              <TextInput style={[styles.input, styles.multiline, { textAlign: rtl.textStart }]} value={draft.instructions} onChangeText={(v) => set({ instructions: v })} maxLength={4000} multiline placeholder={t('aiAgents.form.instructions')} placeholderTextColor={colors.text.secondary as string} />
              <Segments ui={ui} value={draft.language} onChange={(v) => set({ language: v as AiAgent['language'] })}
                options={[{ id: 'auto', label: t('aiAgents.form.languageAuto') }, { id: 'ar', label: 'العربية' }, { id: 'en', label: 'English' }]} />
            </Section>

            <Section ui={ui} title={t('aiAgents.form.coverage')} hint={t('aiAgents.form.coverageHint')}>
              <Chips ui={ui} options={[{ id: 'whatsapp', label: 'WhatsApp' }, { id: 'facebook_messenger', label: 'Messenger' }]} selected={draft.channels} onToggle={(id) => set({ channels: toggle(draft.channels, id) })} />
              <Chips ui={ui} options={shownNumbers} selected={draft.channelIdentifiers} onToggle={(id) => set({ channelIdentifiers: toggle(draft.channelIdentifiers, id) })} />
              <Segments ui={ui} value={draft.hours} onChange={(v) => set({ hours: v as AiAgent['hours'] })}
                options={[{ id: 'always', label: t('aiAgents.form.hoursAlways') }, { id: 'business', label: t('aiAgents.form.hoursBusiness') }, { id: 'after', label: t('aiAgents.form.hoursAfter') }]} />
            </Section>

            <Section ui={ui} title={t('aiAgents.form.actions')}>
              <Chips ui={ui} options={ACTIONS.map((a) => ({ id: a, label: t(`aiAgents.action.${a}`) }))} selected={draft.actions} onToggle={(id) => set({ actions: toggle(draft.actions, id) })} />
            </Section>

            <Section ui={ui} title={t('aiAgents.form.presentation')} hint={t('aiAgents.form.presentationHint')}>
              <Segments ui={ui} value={draft.presentation || 'page'} onChange={(v) => set({ presentation: v as AiAgent['presentation'] })}
                options={[{ id: 'page', label: t('aiAgents.form.presentationPage') }, { id: 'chat', label: t('aiAgents.form.presentationChat') }]} />
            </Section>

            <Section ui={ui} title={t('aiAgents.form.products')} hint={t('aiAgents.form.productsHint')}>
              <Chips ui={ui} options={catalogues} selected={draft.catalogueIds} onToggle={(id) => set({ catalogueIds: toggle(draft.catalogueIds, id) })} empty={t('aiAgents.form.noCatalogues')} />
            </Section>

            <Section ui={ui} title={t('aiAgents.form.handoff')} hint={t('aiAgents.form.handoffHint')}>
              <Segments ui={ui} value={draft.handoff.target} onChange={(v) => setHandoff({ target: v as AiAgent['handoff']['target'] })}
                options={[{ id: 'previous', label: t('aiAgents.form.toPrevious') }, { id: 'user', label: t('aiAgents.form.toUser') }, { id: 'department', label: t('aiAgents.form.toDepartment') }]} />
              {draft.handoff.target === 'user' && (
                <Chips ui={ui} options={people} selected={draft.handoff.userId ? [draft.handoff.userId] : []} onToggle={(id) => setHandoff({ userId: draft.handoff.userId === id ? undefined : id })} />
              )}
              {draft.handoff.target !== 'user' && (
                <>
                  {draft.handoff.target === 'previous' && <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('aiAgents.form.previousFallback')}</Text>}
                  <Chips ui={ui} options={departments} selected={draft.handoff.departmentId ? [draft.handoff.departmentId] : []} onToggle={(id) => setHandoff({ departmentId: draft.handoff.departmentId === id ? undefined : id })} />
                </>
              )}
              <Toggle ui={ui} label={t('aiAgents.form.onAskHuman')} value={draft.handoff.onAskHuman} onChange={(v) => setHandoff({ onAskHuman: v })} />
              <Toggle ui={ui} label={t('aiAgents.form.onNegative')} value={draft.handoff.onNegative} onChange={(v) => setHandoff({ onNegative: v })} />
              <View style={[styles.toggleRow, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.toggleLabel, { textAlign: rtl.textStart }]}>{t('aiAgents.form.maxTurns')}</Text>
                <TextInput style={[styles.input, styles.small]} keyboardType="number-pad" value={draft.handoff.maxTurns ? String(draft.handoff.maxTurns) : ''}
                  onChangeText={(v) => setHandoff({ maxTurns: Math.min(200, Number(v.replace(/\D/g, '')) || 0) })} placeholder="—" placeholderTextColor={colors.text.secondary as string} />
              </View>
              <TextInput style={[styles.input, { textAlign: rtl.textStart }]} value={keywords} onChangeText={setKeywords} placeholder={t('aiAgents.form.keywords')} placeholderTextColor={colors.text.secondary as string} />
            </Section>

            <Section ui={ui} title={t('aiAgents.form.other')}>
              <Toggle ui={ui} label={t('aiAgents.form.isDefault')} value={draft.isDefault} onChange={(v) => set({ isDefault: v })} />
              <Toggle ui={ui} label={t('aiAgents.form.paused')} value={draft.paused} onChange={(v) => set({ paused: v })} />
            </Section>
          </ScrollView>
          <View style={[styles.footer, { flexDirection: rtl.rowDirection }]}>
            <TouchableOpacity style={styles.cancel} onPress={onClose}><Text style={styles.cancelText}>{t('common.cancel')}</Text></TouchableOpacity>
            <TouchableOpacity style={styles.save} disabled={saving} onPress={save}>
              {saving ? <ActivityIndicator size="small" color="white" /> : <Text style={styles.saveText}>{t('common.save')}</Text>}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}


type UI = { styles: ReturnType<typeof getStyles>; rtl: ReturnType<typeof useRTLStyles> };

function Section({ ui, title, hint, children }: { ui: UI; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <View style={ui.styles.section}>
      <Text style={[ui.styles.sectionTitle, { textAlign: ui.rtl.textStart }]}>{title}</Text>
      {!!hint && <Text style={[ui.styles.hint, { textAlign: ui.rtl.textStart }]}>{hint}</Text>}
      {children}
    </View>
  );
}

function Segments({ ui, value, options, onChange }: { ui: UI; value: string; options: Option[]; onChange: (v: string) => void }) {
  return (
    <View style={[ui.styles.segments, { flexDirection: ui.rtl.rowDirection }]}>
      {options.map((o) => (
        <TouchableOpacity key={o.id} style={[ui.styles.segment, value === o.id && ui.styles.segmentOn]} onPress={() => onChange(o.id)}>
          <Text style={[ui.styles.segmentText, value === o.id && ui.styles.segmentTextOn]}>{o.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

function Chips({ ui, options, selected, onToggle, empty }: { ui: UI; options: Option[]; selected: string[]; onToggle: (id: string) => void; empty?: string }) {
  return (
    <View style={[ui.styles.chips, { flexDirection: ui.rtl.rowDirection }]}>
      {!options.length && !!empty && <Text style={ui.styles.hint}>{empty}</Text>}
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <TouchableOpacity key={o.id} style={[ui.styles.chip, on && ui.styles.chipOn]} onPress={() => onToggle(o.id)}>
            {on && <Ionicons name="checkmark" size={12} color={colors.primary as string} />}
            <Text style={[ui.styles.chipText, on && ui.styles.chipTextOn]} numberOfLines={1}>{o.label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

function Toggle({ ui, label, value, onChange }: { ui: UI; label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <View style={[ui.styles.toggleRow, { flexDirection: ui.rtl.rowDirection }]}>
      <Text style={[ui.styles.toggleLabel, { textAlign: ui.rtl.textStart }]}>{label}</Text>
      <Switch value={value} onValueChange={onChange} />
    </View>
  );
}

const getStyles = () => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', alignItems: 'center', justifyContent: 'center', padding: spacing.md },
  sheet: { width: '100%', maxWidth: 620, maxHeight: '92%', backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, overflow: 'hidden' },
  header: { alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.border },
  title: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  body: { paddingHorizontal: spacing.md, paddingTop: spacing.md },
  section: { gap: spacing.xs },
  sectionTitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  input: { minHeight: 38, paddingHorizontal: spacing.sm, paddingVertical: 6, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border, color: colors.text.primary, backgroundColor: colors.background.base, fontSize: typography.fontSize.sm },
  multiline: { minHeight: 90, textAlignVertical: 'top' },
  small: { width: 70, textAlign: 'center' },
  segments: { flexWrap: 'wrap', gap: 6 },
  segment: { paddingHorizontal: spacing.sm, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: colors.border },
  segmentOn: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary as string, 0.1) },
  segmentText: { fontSize: typography.fontSize.xs, color: colors.text.primary },
  segmentTextOn: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  chips: { flexWrap: 'wrap', gap: 6 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 4, maxWidth: 240, paddingHorizontal: spacing.sm, paddingVertical: 5, borderRadius: 14, borderWidth: 1, borderColor: colors.border },
  chipOn: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary as string, 0.08) },
  chipText: { fontSize: typography.fontSize.xs, color: colors.text.primary },
  chipTextOn: { color: colors.primary },
  toggleRow: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
  toggleLabel: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
  footer: { justifyContent: 'flex-end', gap: spacing.sm, padding: spacing.md, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border },
  cancel: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  cancelText: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  save: { minWidth: 96, alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: designTokens.borderRadius.sm, backgroundColor: colors.primary },
  saveText: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: 'white' },
});
